import { RootState } from '@/lib/store'
import { Action, PayloadAction } from '@reduxjs/toolkit'
import { createApi, fetchBaseQuery, retry } from '@reduxjs/toolkit/query/react'
import { HYDRATE } from 'next-redux-wrapper'
import Session from 'supertokens-web-js/recipe/session'

export const TAG_TYPES = ['Agent', 'UserQuota', 'User', 'Workflow', 'WorkflowMetadata'] as const
export type TagType = (typeof TAG_TYPES)[number]

function isHydrateAction(action: Action): action is PayloadAction<RootState, string, never, never> {
  return action.type === HYDRATE
}

const baseQuery = fetchBaseQuery({
  baseUrl: process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI,
  credentials: 'include',
})

const baseQueryWithReauthAndRetry = retry(
  async (args, api, extraOptions) => {
    const result = await baseQuery(args, api, extraOptions)

    // If the request fails with a 401, try to refresh the session.
    if (
      result.error?.status === 401 ||
      ('error' in (result.error ?? {}) && (result.error as { error: string }).error.includes('401'))
    ) {
      // Check if a SuperTokens session exists
      const hasSession = await Session.doesSessionExist()
      if (!hasSession) {
        // Try to create an anonymous session by fetching the user
        const userResponse = await baseQuery(
          { url: 'v1/users/me', method: 'GET' },
          api,
          extraOptions
        )
        if (!userResponse.error) {
          // Retry the original request
          return await baseQuery(args, api, extraOptions)
        }
        // If fetching the user fails, fall through to fail
      }
      retry.fail(
        {
          status: 401,
          data: {
            code: '401',
            message: 'Unauthorized',
          },
        },
        result.meta
      )
    }

    return result
  },
  { maxRetries: 3 }
)

export const api = createApi({
  reducerPath: 'api',
  baseQuery: baseQueryWithReauthAndRetry,
  tagTypes: TAG_TYPES,
  extractRehydrationInfo(action, { reducerPath }): any {
    if (isHydrateAction(action)) {
      return action.payload[reducerPath]
    }
  },
  endpoints: () => ({}),
})
